import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import { traerCatalogo, ordenarPor, PUERTOS, MODALIDADES, ASEGURADO_OPCIONES } from './catalogos.js';

/**
 * Alta / edición de Contenedores. V2 usa una página completa en vez de los dos
 * formularios de V1, pero con EXACTAMENTE los campos de cada uno:
 *
 *  - ALTA = `ContenedoresCreate.jsx` de V1: Fecha (solo lectura = hoy), Cliente,
 *    Naviera, Agente Aduanal, Forwarder, Contenedor, Puerto, Asegurado, Modalidad,
 *    Fecha ETA, Dirección de Entrega, Observaciones, TOTAL de piezas/importe y UNA
 *    línea de artículo (en V1 el botón "+" de agregar líneas está comentado; el "-"
 *    siempre queda deshabilitado). Todo requerido (V1 usa `required` HTML en cada
 *    campo) y candado extra "Debes completar los datos de Articulos, Cliente, Agente
 *    y Forwarder". Terminal/Programa/Patente se mandan con los mismos IDs fijos de V1.
 *
 *  - EDICIÓN = modal `jalaInfo/editContenedores` de `ListadoContenedores.jsx`:
 *    Contenedor, Fecha (solo lectura), Número Interno (solo lectura), Cliente,
 *    Naviera, Agente Aduanal, Forwarder, Puerto, Terminal, Pedimento, Programa,
 *    Patente, Modalidad, Asegurado, Dirección de Entrega, Observaciones,
 *    Observaciones Internas. Las partidas de artículos, el Status/hitos y el mapa se
 *    editan desde los botones de la fila del listado (igual que V1).
 */

// IDs fijos que V1 manda en el alta (`ContenedoresCreate.jsx`, `saveOrdenCompra`).
const TERMINAL_ALTA = '659751b809da69446cc05f3d';
const PROGRAMA_ALTA = '65984b7d111f341061f6c353';
const PATENTE_ALTA = '66db35dea6b7de33346cd392';

const hoy = () => new Date().toISOString().slice(0, 10);
const fmtUSD = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n || 0);

const VALORES_INICIALES = {
  fecha: hoy(),
  contenedor: '',
  numeroInterno: '',
  clientes: '',
  navieras: '',
  agentes: '',
  forwarders: '',
  terminales: '',
  programas: '',
  patentes: '',
  puerto: '',
  modalidad: '',
  asegurado: '',
  direccionEntrega: 'NA',
  observaciones: 'NA',
  observacionesInternas: 'NA',
  pedimento: '',
  fechaETA: '',
};

function Campo({ label, className, children }) {
  return (
    <div className={className}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'input-base w-full';
const selectCls = 'select-base w-full';

export default function ContenedorFormPage() {
  const { id } = useParams();
  const esNuevo = !id || id === 'nuevo';
  const navigate = useNavigate();
  const { hasPermiso } = useContext(AuthContext);

  const puedeGuardar = esNuevo ? hasPermiso('contenedores:crear') : hasPermiso('contenedores:editar');

  const [valores, setValores] = useState(VALORES_INICIALES);
  const [idContenedor, setIdContenedor] = useState(null);
  const [cargando, setCargando] = useState(!esNuevo);
  const [guardando, setGuardando] = useState(false);

  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [terminales, setTerminales] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [patentes, setPatentes] = useState([]);
  const [articulosCatalogo, setArticulosCatalogo] = useState([]);

  // Alta: una sola línea de artículo (V1: `inputFields` con 1 elemento, "+" comentado).
  const [linea, setLinea] = useState({ articulos: '', cantidad: 1, descripcion: 'NA', precio: 1 });
  const totalLinea = (parseFloat(linea.cantidad) || 0) * (parseFloat(linea.precio) || 0);
  const totalesLineas = useMemo(
    () => ({ totalCantidad: parseFloat(linea.cantidad) || 0, totalGeneral: totalLinea }),
    [linea.cantidad, totalLinea]
  );

  useEffect(() => {
    traerCatalogo('/clientes').then((r) => setClientes(ordenarPor(r, 'nombre_comercial'))).catch(() => setClientes([]));
    traerCatalogo('/proveedores').then((r) => setProveedores(ordenarPor(r))).catch(() => setProveedores([]));
    if (esNuevo) {
      traerCatalogo('/articulos').then((r) => setArticulosCatalogo(ordenarPor(r))).catch(() => setArticulosCatalogo([]));
    } else {
      traerCatalogo('/terminales').then((r) => setTerminales(ordenarPor(r))).catch(() => setTerminales([]));
      traerCatalogo('/programas').then((r) => setProgramas(ordenarPor(r))).catch(() => setProgramas([]));
      traerCatalogo('/patentes').then((r) => setPatentes(ordenarPor(r))).catch(() => setPatentes([]));
    }
  }, [esNuevo]);

  const porTipo = (tipo) => proveedores.filter((p) => p.tipo === tipo);

  useEffect(() => {
    if (esNuevo) return;
    setCargando(true);
    apiClient.get(`/contenedores/${id}`)
      .then(({ data }) => {
        const doc = data.data;
        const unoId = (arr) => (Array.isArray(arr) && arr[0] ? arr[0]._id : '');
        setIdContenedor(doc.idContenedor);
        setValores((v) => ({
          ...v,
          ...Object.fromEntries(Object.keys(v).map((k) => [k, doc[k] ?? v[k]])),
          // V1 muestra "Cliente-folio" (se arma en pantalla, no se guarda así).
          numeroInterno: `${Array.isArray(doc.clientes) && doc.clientes[0] ? doc.clientes[0].nombre_comercial : ''}-${doc.idContenedor}`,
          clientes: unoId(doc.clientes),
          navieras: unoId(doc.navieras),
          agentes: unoId(doc.agentes),
          forwarders: unoId(doc.forwarders),
          terminales: unoId(doc.terminales),
          programas: unoId(doc.programas),
          patentes: unoId(doc.patentes),
        }));
      })
      .catch((e) => Swal.fire({ icon: 'error', title: 'No se pudo cargar el contenedor', text: e.message }))
      .finally(() => setCargando(false));
  }, [id, esNuevo]);

  function set(key, value) {
    setValores((v) => ({ ...v, [key]: value }));
  }

  async function guardarAlta() {
    if (!linea.articulos || !valores.clientes || !valores.agentes || !valores.forwarders) {
      Swal.fire({
        icon: 'error', title: 'Oops...', text: 'Something went wrong!',
        footer: 'Debes completar los datos de Articulos, Cliente, Agente y Forwarder',
      });
      return;
    }
    setGuardando(true);
    try {
      const { data } = await apiClient.post('/contenedores', {
        fecha: valores.fecha,
        navieras: valores.navieras,
        clientes: valores.clientes,
        agentes: valores.agentes,
        forwarders: valores.forwarders,
        articulos: linea.articulos,
        numeroInterno: '0',
        contenedor: valores.contenedor.trim(),
        etaEstimada: valores.fechaETA,
        etaReal: valores.fechaETA,
        totalGastos: 0,
        totalGeneral: totalesLineas.totalGeneral,
        totalCantidad: totalesLineas.totalCantidad,
        observaciones: valores.observaciones,
        totalCargos: 0,
        puerto: valores.puerto,
        modalidad: valores.modalidad,
        direccionEntrega: valores.direccionEntrega,
        terminales: TERMINAL_ALTA,
        programas: PROGRAMA_ALTA,
        patentes: PATENTE_ALTA,
        asegurado: valores.asegurado,
        rastreo: 'No',
      });
      await apiClient.post('/articulos-contenedor', {
        fecha: valores.fecha,
        contenedores: data.data._id,
        navieras: valores.navieras,
        clientes: valores.clientes,
        agentes: valores.agentes,
        forwarder: valores.forwarders,
        articulos: linea.articulos,
        cantidad: parseFloat(linea.cantidad),
        descripcion: linea.descripcion || 'NA',
        precio: parseFloat(linea.precio),
        total: totalLinea,
      });
      Swal.fire({ icon: 'success', title: 'Good job!', text: 'Creado con exito', timer: 1500, showConfirmButton: false });
      navigate('/contenedores');
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: error.message });
    } finally {
      setGuardando(false);
    }
  }

  async function guardarEdicion() {
    setGuardando(true);
    try {
      const payload = {
        fecha: valores.fecha,
        navieras: valores.navieras,
        clientes: valores.clientes,
        agentes: valores.agentes,
        forwarders: valores.forwarders,
        observaciones: valores.observaciones,
        observacionesInternas: valores.observacionesInternas,
        contenedor: valores.contenedor.trim(),
        puerto: valores.puerto,
        terminales: valores.terminales,
        pedimento: valores.pedimento,
        modalidad: valores.modalidad,
        asegurado: valores.asegurado,
        direccionEntrega: valores.direccionEntrega,
        programas: valores.programas,
        patentes: valores.patentes,
      };
      // Los ref vacíos (sin seleccionar) no deben mandarse como '' (Joi objectId los rechaza).
      ['navieras', 'clientes', 'agentes', 'forwarders', 'terminales', 'programas', 'patentes'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (!payload.asegurado) delete payload.asegurado;
      await apiClient.patch(`/contenedores/${id}`, payload);
      Swal.fire({ icon: 'success', title: 'Good job!', text: 'Actualizado con exito', timer: 1500, showConfirmButton: false });
      navigate('/contenedores');
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: error.message || 'Something went wrong!', footer: error.message });
    } finally {
      setGuardando(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    return esNuevo ? guardarAlta() : guardarEdicion();
  }

  if (cargando) return <div className="text-slate-500">Cargando...</div>;

  const optsProv = (tipo) => porTipo(tipo).map((p) => <option key={p._id} value={p._id}>{p.name}</option>);
  const optsCat = (arr) => arr.map((a) => <option key={a._id} value={a._id}>{a.name}</option>);

  const botones = (
    <div className="flex gap-2">
      {puedeGuardar && (
        <button type="submit" disabled={guardando} className="btn-primary">
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
      )}
      <button type="button" onClick={() => navigate('/contenedores')} className="btn-outline">
        Regresar
      </button>
    </div>
  );

  // ------------------------------- ALTA (V1: ContenedoresCreate) -------------------------------
  if (esNuevo) {
    return (
      <form onSubmit={handleSubmit} className="card p-4 space-y-4 max-w-6xl">
        <h1 className="text-xl font-semibold text-slate-900 text-center">Nuevo Contenedor</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Campo label="Fecha">
            <input type="date" className={inputCls} value={valores.fecha} required disabled />
          </Campo>
          <Campo label="Cliente">
            <select className={selectCls} value={valores.clientes} required onChange={(e) => set('clientes', e.target.value)}>
              <option value="">Selecciona</option>
              {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
            </select>
          </Campo>
          <Campo label="Naviera">
            <select className={selectCls} value={valores.navieras} required onChange={(e) => set('navieras', e.target.value)}>
              <option value="">Selecciona</option>
              {optsProv('Naviera')}
            </select>
          </Campo>
          <Campo label="Agente Aduanal">
            <select className={selectCls} value={valores.agentes} required onChange={(e) => set('agentes', e.target.value)}>
              <option value="">Selecciona</option>
              {optsProv('Agente')}
            </select>
          </Campo>

          <Campo label="Forwarder">
            <select className={selectCls} value={valores.forwarders} required onChange={(e) => set('forwarders', e.target.value)}>
              <option value="">Selecciona</option>
              {optsProv('Forwarder')}
            </select>
          </Campo>
          <Campo label="Contenedor">
            <input className={inputCls} placeholder="Contenedor" value={valores.contenedor} required onChange={(e) => set('contenedor', e.target.value)} />
          </Campo>
          <Campo label="Puerto">
            <select className={selectCls} value={valores.puerto} required onChange={(e) => set('puerto', e.target.value)}>
              <option value="">Selecciona</option>
              {PUERTOS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Campo>
          <Campo label="Asegurado">
            <select className={selectCls} value={valores.asegurado} required onChange={(e) => set('asegurado', e.target.value)}>
              <option value="">Selecciona</option>
              {ASEGURADO_OPCIONES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Campo>

          <Campo label="Modalidad">
            <select className={selectCls} value={valores.modalidad} required onChange={(e) => set('modalidad', e.target.value)}>
              <option value="">Selecciona</option>
              {MODALIDADES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Campo>
          <Campo label="Fecha ETA">
            <input type="date" className={inputCls} value={valores.fechaETA} required onChange={(e) => set('fechaETA', e.target.value)} />
          </Campo>
          <Campo label="Direccion de Entrega" className="sm:col-span-2">
            <input className={inputCls} placeholder="Direccion Entrega" value={valores.direccionEntrega} required onChange={(e) => set('direccionEntrega', e.target.value)} />
          </Campo>

          <Campo label="Observaciones" className="sm:col-span-2">
            <textarea rows={2} className={inputCls} placeholder="Observaciones" value={valores.observaciones} required onChange={(e) => set('observaciones', e.target.value)} />
          </Campo>
        </div>

        <h4 className="text-lg font-semibold text-slate-800">
          TOTAL {totalesLineas.totalCantidad} pzas. / {fmtUSD(totalesLineas.totalGeneral)}
        </h4>

        {/* Tabla Articulos */}
        <div className="grid grid-cols-12 gap-2 text-sm font-medium text-slate-600">
          <div className="col-span-3">Articulos</div>
          <div className="col-span-3">Descripcion</div>
          <div className="col-span-2">Cantidad</div>
          <div className="col-span-2">Precio USD</div>
          <div className="col-span-2">Total</div>
        </div>
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-3">
            <select className={selectCls} value={linea.articulos} required onChange={(e) => setLinea((l) => ({ ...l, articulos: e.target.value }))}>
              <option value="">Selecciona</option>
              {optsCat(articulosCatalogo)}
            </select>
          </div>
          <div className="col-span-3">
            <input className={inputCls} placeholder="descripcion" value={linea.descripcion} required onChange={(e) => setLinea((l) => ({ ...l, descripcion: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <input type="number" min="0" step="any" className={inputCls} placeholder="Cantidad" value={linea.cantidad} required onChange={(e) => setLinea((l) => ({ ...l, cantidad: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <input type="number" min="0" step="any" className={inputCls} placeholder="Precio" value={linea.precio} required onChange={(e) => setLinea((l) => ({ ...l, precio: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <input type="number" className={inputCls} placeholder="Total" value={totalLinea} disabled />
          </div>
        </div>

        {botones}
      </form>
    );
  }

  // (En V1 el modal de edición no es un <form>, así que sus campos NO tienen validación `required` efectiva.)
// ------------------------------- EDICIÓN (V1: modal de ListadoContenedores) -------------------------------
  return (
    <form onSubmit={handleSubmit} className="card p-4 space-y-4 max-w-6xl">
      <h1 className="text-xl font-semibold text-slate-900">
        Editar Contenedor {idContenedor}
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Campo label="Contenedor" className="lg:col-span-4 lg:max-w-xs">
          <input className={inputCls} placeholder="Contenedor" value={valores.contenedor} onChange={(e) => set('contenedor', e.target.value)} />
        </Campo>

        <Campo label="Fecha">
          <input type="date" className={inputCls} value={valores.fecha} disabled />
        </Campo>
        <Campo label="Numero Interno">
          <input className={inputCls} placeholder="numeroInterno" value={valores.numeroInterno} disabled />
        </Campo>
        <Campo label="Cliente">
          <select className={selectCls} value={valores.clientes} onChange={(e) => set('clientes', e.target.value)}>
            <option value="">Selecciona</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
          </select>
        </Campo>
        <Campo label="Naviera">
          <select className={selectCls} value={valores.navieras} onChange={(e) => set('navieras', e.target.value)}>
            <option value="">Selecciona</option>
            {optsProv('Naviera')}
          </select>
        </Campo>

        <Campo label="Agente Aduanal">
          <select className={selectCls} value={valores.agentes} onChange={(e) => set('agentes', e.target.value)}>
            <option value="">Selecciona</option>
            {optsProv('Agente')}
          </select>
        </Campo>
        <Campo label="Forwarder">
          <select className={selectCls} value={valores.forwarders} onChange={(e) => set('forwarders', e.target.value)}>
            <option value="">Selecciona</option>
            {optsProv('Forwarder')}
          </select>
        </Campo>
        <Campo label="Puerto">
          <select className={selectCls} value={valores.puerto} onChange={(e) => set('puerto', e.target.value)}>
            <option value="">Selecciona</option>
            {PUERTOS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Campo>
        <Campo label="Terminal">
          <select className={selectCls} value={valores.terminales} onChange={(e) => set('terminales', e.target.value)}>
            <option value="">Selecciona</option>
            {optsCat(terminales)}
          </select>
        </Campo>

        <Campo label="Pedimento">
          <input className={inputCls} placeholder="Pedimento" value={valores.pedimento} onChange={(e) => set('pedimento', e.target.value)} />
        </Campo>
        <Campo label="Programa">
          <select className={selectCls} value={valores.programas} onChange={(e) => set('programas', e.target.value)}>
            <option value="">Selecciona</option>
            {optsCat(programas)}
          </select>
        </Campo>
        <Campo label="Patente">
          <select className={selectCls} value={valores.patentes} onChange={(e) => set('patentes', e.target.value)}>
            <option value="">Selecciona</option>
            {optsCat(patentes)}
          </select>
        </Campo>
        <Campo label="Modalidad">
          <select className={selectCls} value={valores.modalidad} onChange={(e) => set('modalidad', e.target.value)}>
            <option value="">Selecciona</option>
            {MODALIDADES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Campo>
        <Campo label="Asegurado">
          <select className={selectCls} value={valores.asegurado} onChange={(e) => set('asegurado', e.target.value)}>
            <option value="">Selecciona</option>
            {ASEGURADO_OPCIONES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </Campo>

        <Campo label="Direccion de Entrega" className="sm:col-span-2">
          <input className={inputCls} placeholder="Direccion Entrega" value={valores.direccionEntrega} onChange={(e) => set('direccionEntrega', e.target.value)} />
        </Campo>
        <Campo label="Observaciones" className="sm:col-span-2 lg:col-span-4 lg:max-w-2xl">
          <textarea rows={2} className={inputCls} placeholder="Observaciones" value={valores.observaciones} onChange={(e) => set('observaciones', e.target.value)} />
        </Campo>
        <Campo label="Observaciones Internas" className="sm:col-span-2 lg:col-span-4 lg:max-w-2xl">
          <textarea rows={2} className={inputCls} placeholder="ObservacionesInternas" value={valores.observacionesInternas} onChange={(e) => set('observacionesInternas', e.target.value)} />
        </Campo>
      </div>

      {botones}
    </form>
  );
}
